import { setPanelContent, getShadowRoot, setFabPulse, bumpUnread } from "../../ui/container";
import { playSend, playReceive } from "../../ui/sound";
import { resolveApiBase } from "../../core/origin";
import { t } from "../../ui/i18n";
import { renderVoicePanel } from "../voice/render";
import type { Locale, FormCollectionConfig } from "../../types";

var storedLocale: Locale = "zh-CN";
function tt(key: any, ...args: string[]): string { return t(key, storedLocale, ...args); }

var textWs: WebSocket | null = null;
var chatMessages: Array<{ role: "user" | "agent" | "_typing_"; text: string }> = [];
// 客服消息轮询（单例，跨面板开关持续运行，使未读在面板关闭时也能累计）
var staffPollTimer: any = null;
var staffLastCheck = "";
var staffFirstPoll = true;
var storedReceptionistId: number | null = null;
var storedReceptionistName = "AI 助手";
var storedWidgetId = "";
var onBackToHome: (() => void) | null = null;
// API 基地址必须**惰性**读取：本模块在 SDK 脚本加载时即被求值，而 __oc_apiBase__ 由
// init() 之后才设定。此前用模块级 var 捕获会回退到 location.origin（第三方站点）→ 跨站 404。
function apiBaseUrl(): string { return resolveApiBase(); }

function getVisitorKey(): string {
  try { var k = localStorage.getItem("oc_visitor_key"); if (k) return k; } catch (e) {}
  var key = "v_" + Date.now() + "_" + Math.random().toString(36).slice(2, 8);
  try { localStorage.setItem("oc_visitor_key", key); } catch (e) {}
  return key;
}

function saveMessage(role: "user" | "agent", text: string): void {
  try {
    fetch(apiBaseUrl() + "/widget/conversation-message", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        widgetId: storedWidgetId,
        receptionistId: storedReceptionistId,
        visitorName: getVisitorKey(),
        message: text,
        senderType: role === "user" ? "visitor" : "agent",
        isStaff: 0,
      }),
    }).catch(function() { /* silent fail */ });
  } catch (e) { /* silent */ }
}

var storedTextChatWelcome = "你好，有什么可以帮你的？";
var storedQuickReplies: string[] = [];

// ===== 信息表单收集 =====
var storedFormConfig: FormCollectionConfig | null = null;
var visitorTurnCount = 0;
var formStatus: "none" | "submitted" | "skipped" = "none";
var formInviteCount = 0;       // 已邀请次数（最多 2：首次 + 跳过后再邀请一次）
var pendingFormInvite = false; // 本轮 AI 回复后是否要展示表单
var pendingFormTimer: any = null;

function formMemKey(): string { return "oc_form_" + storedWidgetId; }

/** 读取本访客是否已提交过表单（已提交则全程不再邀请） */
function loadFormMemory(): void {
  formStatus = "none"; formInviteCount = 0; pendingFormInvite = false; visitorTurnCount = 0;
  try {
    var v = localStorage.getItem(formMemKey());
    if (v === "submitted") formStatus = "submitted";
  } catch (e) { /* ignore */ }
}

function flushFormInvite(): void {
  if (!pendingFormInvite) return;
  pendingFormInvite = false;
  if (pendingFormTimer) { clearTimeout(pendingFormTimer); pendingFormTimer = null; }
  maybeShowForm();
}

/** 按访客轮次判断是否应在本轮邀请填写表单 */
function scheduleFormInviteIfDue(): void {
  if (!storedFormConfig || !storedFormConfig.enabled || formStatus === "submitted") return;
  var turn = storedFormConfig.triggerTurn === 3 ? 3 : 2;
  var due =
    (formInviteCount === 0 && visitorTurnCount === turn) ||                       // 首次邀请
    (formStatus === "skipped" && formInviteCount === 1 && visitorTurnCount === turn + 3); // 跳过后再邀请一次
  if (!due) return;
  pendingFormInvite = true;
  if (pendingFormTimer) clearTimeout(pendingFormTimer);
  // 兜底：人工接管/无 AI 回复时也能展示
  pendingFormTimer = setTimeout(flushFormInvite, 6000);
}

function maybeShowForm(): void {
  if (!storedFormConfig || !storedFormConfig.enabled || formStatus === "submitted") return;
  if (formInviteCount >= 2) return;
  var root = getShadowRoot(); if (!root) return;
  if (root.getElementById("oc-tc-form")) return; // 已有表单卡片
  formInviteCount++;
  var invite = (storedFormConfig.inviteText || "").trim() || "为了更好地为您服务，方便留下您的联系方式吗？";
  chatMessages.push({ role: "agent", text: invite });
  saveMessage("agent", invite);
  refreshMessages();
  renderFormNode();
}

/** 把表单渲染为 tc-body 下的独立持久节点（不进 #tc-messages，避免轮询刷新清空已填值） */
function renderFormNode(): void {
  var root = getShadowRoot(); if (!root) return;
  var body = root.getElementById("tc-body"); if (!body) return;
  var fields = (storedFormConfig && storedFormConfig.fields) || [];
  var col = c();
  var rows = fields.map(function(f) {
    return '<div style="margin-bottom:8px">' +
      '<div style="font-size:11px;color:#64748b;margin-bottom:3px">' + esc(f.label) + (f.required ? '<span style="color:#ef4444"> *</span>' : '') + '</div>' +
      '<input data-fk="' + esc(f.key) + '" type="' + esc(f.type) + '" placeholder="请输入' + esc(f.label) + '" style="width:100%;box-sizing:border-box;padding:8px 10px;border:1px solid #e5e7eb;border-radius:8px;font-size:13px;outline:none" />' +
      '<div data-err="' + esc(f.key) + '" style="display:none;color:#ef4444;font-size:11px;margin-top:2px"></div>' +
    '</div>';
  }).join("");
  var wrap = document.createElement("div");
  wrap.id = "oc-tc-form";
  wrap.style.cssText = "margin:0 16px 14px 16px;padding:12px 14px;background:#fff;border:1px solid #eee;border-radius:14px";
  wrap.innerHTML = rows +
    '<div style="display:flex;gap:8px;margin-top:6px">' +
      '<button id="oc-form-submit" style="flex:1;padding:9px 0;border:none;border-radius:8px;background:' + col + ';color:#fff;font-size:13px;cursor:pointer">提交</button>' +
      '<button id="oc-form-skip" style="padding:9px 14px;border:1px solid #e5e7eb;border-radius:8px;background:#fff;color:#64748b;font-size:13px;cursor:pointer">稍后再说</button>' +
    '</div>';
  body.appendChild(wrap);
  body.scrollTop = body.scrollHeight;
  wrap.querySelector("#oc-form-submit")?.addEventListener("click", submitForm);
  wrap.querySelector("#oc-form-skip")?.addEventListener("click", skipForm);
}

function removeFormNode(): void {
  var root = getShadowRoot(); if (!root) return;
  var n = root.getElementById("oc-tc-form");
  if (n && n.parentNode) n.parentNode.removeChild(n);
}

var EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function submitForm(): void {
  var root = getShadowRoot(); if (!root) return;
  var form = root.getElementById("oc-tc-form"); if (!form) return;
  var fields = (storedFormConfig && storedFormConfig.fields) || [];
  var values: Record<string, string> = {};
  var ok = true;
  for (var i = 0; i < fields.length; i++) {
    var f = fields[i];
    var inp = form.querySelector('input[data-fk="' + f.key + '"]') as HTMLInputElement | null;
    var errEl = form.querySelector('[data-err="' + f.key + '"]') as HTMLElement | null;
    var val = (inp?.value || "").trim();
    values[f.key] = val;
    var err = "";
    if (f.required && !val) err = "必填";
    else if (val && f.type === "email" && !EMAIL_RE.test(val)) err = "邮箱格式不正确";
    else if (val && f.type === "tel" && val.replace(/[^\d]/g, "").length < 6) err = "手机号格式不正确";
    if (errEl) { errEl.textContent = err; errEl.style.display = err ? "block" : "none"; }
    if (inp) inp.style.borderColor = err ? "#ef4444" : "#e5e7eb";
    if (err) ok = false;
  }
  if (!ok) return;

  var submitBtn = form.querySelector("#oc-form-submit") as HTMLButtonElement | null;
  if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = "提交中…"; }

  fetch(apiBaseUrl() + "/widget/lead", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      widgetId: storedWidgetId,
      visitorId: getVisitorKey(),
      name: values.name || "",
      phone: values.phone || "",
      email: values.email || "",
      actionType: "form_submit",
      metadata: { company: values.company || "", formType: storedFormConfig?.formType || "", source: "text_chat_form" },
    }),
  }).then(function(r) { return r.json().catch(function() { return {}; }); })
  .then(function() {
    formStatus = "submitted";
    try { localStorage.setItem(formMemKey(), "submitted"); } catch (e) {}
    removeFormNode();
    var thanks = "已收到，谢谢！我们会尽快与您联系 🎉";
    chatMessages.push({ role: "agent", text: thanks });
    saveMessage("agent", thanks);
    refreshMessages();
  }).catch(function() {
    if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = "提交"; }
  });
}

function skipForm(): void {
  formStatus = "skipped";
  try { localStorage.setItem(formMemKey(), "skipped"); } catch (e) {}
  removeFormNode();
}

export function renderTextChatPanel(
  _locale: Locale, widgetId: string,
  receptionistId?: number | null, receptionistName?: string | null,
  onBack?: () => void,
  textChatWelcome?: string,
  quickReplies?: string[],
  formConfig?: FormCollectionConfig | null,
): void {
  chatMessages = [];
  storedLocale = _locale || "zh-CN";
  storedReceptionistId = receptionistId || null;
  storedReceptionistName = receptionistName || tt("tc.recName");
  storedWidgetId = widgetId || "";
  // 重新打开会话：客服消息轮询重新基线化（重新拉取最近客服消息回显，但不触发提示音/未读）
  staffLastCheck = "";
  staffFirstPoll = true;
  onBackToHome = onBack || null;
  storedTextChatWelcome = textChatWelcome || tt("tc.welcome");
  storedQuickReplies = quickReplies || [];
  storedFormConfig = (formConfig && formConfig.enabled) ? formConfig : null;
  loadFormMemory();
  setFabPulse(false);
  if (textWs) { textWs.close(); textWs = null; }
  if (storedTextChatWelcome) {
    chatMessages.push({ role: "agent", text: storedTextChatWelcome });
  }
  setPanelContent(buildHTML());
  bindEvents();
  refreshMessages();
}

function c(): string {
  try { var r = getShadowRoot(); if (r) { var v = getComputedStyle(r.host).getPropertyValue("--oc-primary").trim(); if (v) return v; } } catch (e) {}
  return "#533afd";
}

function buildHTML(): string {
  var col = c();
  var quickRepliesHTML = "";
  if (storedQuickReplies && storedQuickReplies.length > 0) {
    quickRepliesHTML = '<div class="tc-quick-replies">' +
      storedQuickReplies.map(function(r) {
        return '<button class="tc-qr-btn" data-reply="' + esc(r) + '">' + esc(r) + '</button>';
      }).join("") +
      '</div>';
  }
  return '<style>' +
    '.tc-quick-replies{display:flex;flex-wrap:wrap;gap:4px;padding:0 0 6px 0;}' +
    '.tc-qr-btn{padding:3px 10px;border:1px solid ' + col + ';border-radius:14px;background:#fff;color:' + col + ';font-size:11px;cursor:pointer;transition:all 0.15s;white-space:nowrap;}' +
    '.tc-qr-btn:hover{background:' + col + ';color:#fff;}' +
    '.tc-typing-dots{display:flex;gap:4px;align-items:center;padding:8px 14px;background:#f1f5f9;border-radius:14px;}' +
    '.tc-typing-dots span{width:6px;height:6px;border-radius:50%;background:#94a3b8;animation:tc-dot-bounce 1.4s ease-in-out infinite;}' +
    '.tc-typing-dots span:nth-child(2){animation-delay:0.2s;}' +
    '.tc-typing-dots span:nth-child(3){animation-delay:0.4s;}' +
    '@keyframes tc-dot-bounce{0%,80%,100%{transform:scale(0.6);opacity:0.4;}40%{transform:scale(1);opacity:1;}}' +
    '.tc-actions{display:flex;gap:8px;padding:8px 0;border-bottom:1px solid var(--oc-border);margin-bottom:8px;}' +
    '.tc-action-btn{display:flex;align-items:center;gap:5px;padding:6px 12px;border:1px solid var(--oc-border);border-radius:16px;background:var(--oc-bg);color:var(--oc-text-secondary);font-size:12px;cursor:pointer;transition:all 0.15s;}' +
    '.tc-action-btn:hover{border-color:' + col + ';color:' + col + ';}' +
    '.tc-action-btn svg{width:14px;height:14px;}' +
  '</style>' +
    '<div class="tc-root" id="tc-root">' +
    '<div class="tc-actions">' +
      '<button type="button" class="tc-action-btn" id="tc-to-voice"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0122 16.92z"/></svg>' + tt("tc.toVoice") + '</button>' +
      '<button type="button" class="tc-action-btn" id="tc-to-human"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>' + tt("tc.toHuman") + '</button>' +
    '</div>' +
    '<div class="tc-body" id="tc-body"><div id="tc-messages"></div></div>' +
    '<div class="tc-footer">' + quickRepliesHTML +
      '<div class="tc-row-inp">' +
      '<input type="text" id="tc-input" class="tc-input" placeholder="' + tt("tc.inputPlaceholder") + '" autofocus />' +
      '<button id="tc-send" class="tc-send" style="background:' + col + '" title="' + tt("tc.send") + '"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg></button>' +
    '</div></div></div>';
}

function refreshMessages(): void {
  var el = getShadowRoot()?.getElementById("tc-messages"); if (!el) return;
  el.innerHTML = chatMessages.map(function(m) {
    if (m.role === "_typing_") {
      return '<div class="tc-row agent"><div class="tc-av agent-av"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="8" r="5"/><path d="M20 21a8 8 0 00-16 0"/></svg></div><div class="tc-typing-dots"><span></span><span></span><span></span></div></div>';
    }
    var isUser = m.role === "user";
    var av = isUser ? '<div class="tc-av user-av">&#x1F464;</div>' : '<div class="tc-av agent-av"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="8" r="5"/><path d="M20 21a8 8 0 00-16 0"/></svg></div>';
    return '<div class="tc-row ' + (isUser ? "user" : "agent") + '">' + (isUser ? '<div class="tc-bubble">' + esc(m.text) + '</div>' + av : av + '<div class="tc-bubble">' + esc(m.text) + '</div>') + '</div>';
  }).join("");
  var body = getShadowRoot()?.getElementById("tc-body"); if (body) body.scrollTop = body.scrollHeight;
}

function esc(s: string): string { if (!s) return ""; return s.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;"); }

// 拉取客服回复（单例轮询调用）。必须带访客键：后端按 visitor_key 定位会话，缺失会匹配不到会话。
function pollStaffMessages(): void {
  if (!storedWidgetId) return;
  var url = apiBaseUrl() + "/widget/" + encodeURIComponent(storedWidgetId) + "/staff-messages"
    + "?visitorKey=" + encodeURIComponent(getVisitorKey());
  if (staffLastCheck) url += "&since=" + encodeURIComponent(staffLastCheck);
  fetch(url)
    .then(function(r) { return r.json(); })
    .then(function(data) {
      try {
        (window as any).__oc_chatState__ = (window as any).__oc_chatState__ || {};
        (window as any).__oc_chatState__.disableAutoReply = !!(data && typeof data.disableAutoReply === "boolean" && data.disableAutoReply);
      } catch (e) { /* ignore */ }
      if (data && data.messages && data.messages.length) {
        var newCount = 0;
        var maxTs = staffLastCheck;
        for (var i = 0; i < data.messages.length; i++) {
          var m = data.messages[i];
          if (m.created_at && (!maxTs || m.created_at > maxTs)) maxTs = m.created_at;
          var dup = false;
          for (var k = chatMessages.length - 1; k >= 0; k--) {
            if (chatMessages[k].role === "agent" && chatMessages[k].text === m.content) { dup = true; break; }
          }
          if (!dup) { chatMessages.push({ role: "agent", text: m.content }); newCount++; }
        }
        staffLastCheck = maxTs || staffLastCheck;
        if (newCount > 0) {
          refreshMessages();
          // 首次轮询是基线化（含历史/重开），不提示音、不计未读；之后的新客服消息才提示
          if (!staffFirstPoll) { playReceive(); bumpUnread(newCount); }
        }
      }
      staffFirstPoll = false;
    }).catch(function() { /* ignore */ });
}

function startStaffPolling(): void {
  if (staffPollTimer) return;
  staffPollTimer = setInterval(pollStaffMessages, 3000);
}

function connect(): void {
  if (!(window as any).__oc_chatState__) (window as any).__oc_chatState__ = { disableAutoReply: false };
  pollStaffMessages();   // 立即拉一次（重开时快速回显历史/未读消息）
  startStaffPolling();

  var overridden = (window as any).__ONECALL_VOICE_WS__;
  var apiBase = apiBaseUrl();
  var isLocalApi = apiBase.includes("localhost") || apiBase.includes("127.0.0.1");
  var wsUrl = overridden || (isLocalApi ? "ws://127.0.0.1:8765" : "wss://16x.ai/ws");
  var reconnectAttempts = 0;
  var MAX_RECONNECT = 3;
  console.log("[text-chat] WebSocket connecting to:", wsUrl);
  function doConnect() {
    textWs = new WebSocket(wsUrl);
    textWs.onopen = function() {
      console.log("[text-chat] WS open");
      reconnectAttempts = 0;
      textWs!.send(JSON.stringify({ provider: "text_chat", params: { receptionistId: storedReceptionistId || 1, from: "zh", to: "zh" } }));
    };
    textWs.onmessage = function(event) {
      if (typeof event.data !== "string") return;
      console.log("[text-chat] WS recv:", event.data.slice(0, 200));
      try { var msg = JSON.parse(event.data); if (msg.type === "assistant_text" && msg.text) { removeLoading(); chatMessages.push({ role: "agent", text: msg.text }); saveMessage("agent", msg.text); refreshMessages(); flushFormInvite(); playReceive(); bumpUnread(1); } } catch (e) {}
    };
    textWs.onerror = function(e) { console.error("[text-chat] WS error:", e); };
    textWs.onclose = function(e) {
      console.log("[text-chat] WS close, code:", e?.code, "reason:", e?.reason, "attempt:", reconnectAttempts);
      if (reconnectAttempts < MAX_RECONNECT) {
        reconnectAttempts++;
        setTimeout(doConnect, 2000 * reconnectAttempts);
      }
    };
  }
  doConnect();
}

var _loadingTimer: any = null;

function removeLoading(): void {
  if (_loadingTimer) { clearTimeout(_loadingTimer); _loadingTimer = null; }
  chatMessages = chatMessages.filter(function(m) { return m.role !== "_typing_"; });
}

function showLoading(): void {
  removeLoading();
  chatMessages.push({ role: "_typing_", text: "" });
  refreshMessages();
  _loadingTimer = setTimeout(function() {
    removeLoading();
    refreshMessages();
  }, 15000);
}

function send(): void {
  var input = getShadowRoot()?.getElementById("tc-input") as HTMLInputElement;
  var text = input?.value?.trim();
  if (!text) return;
  chatMessages.push({ role: "user", text: text });
  refreshMessages();
  playSend();
  input.value = ""; input.focus();
  // 访客轮次 +1，并判断本轮是否到达表单邀请时机
  visitorTurnCount++;
  scheduleFormInviteIfDue();
  fetch(apiBaseUrl() + "/widget/conversation-message", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      widgetId: storedWidgetId,
      receptionistId: storedReceptionistId,
      visitorName: getVisitorKey(),
      message: text,
      senderType: "visitor",
      isStaff: 0,
    }),
  }).then(function(r) { return r.json(); })
  .then(function(data) {
    if (data.disableAutoReply) { flushFormInvite(); return; } // 人工接管：无 AI 回复，直接展示表单
    if (!textWs || textWs.readyState !== WebSocket.OPEN) {
      removeLoading();
      chatMessages.push({ role: "agent", text: tt("tc.offline") });
      saveMessage("agent", tt("tc.offline"));
      refreshMessages();
      flushFormInvite();
      return;
    }
    showLoading();
    textWs.send(JSON.stringify({ type: "user_text", text: text }));
  }).catch(function() { /* 网络异常时静默失败 */ });
}

function bindEvents(): void {
  connect();
  var root = getShadowRoot(); if (!root) return;
  root.getElementById("tc-send")?.addEventListener("click", send);
  root.getElementById("tc-input")?.addEventListener("keydown", function(e) { if ((e as KeyboardEvent).key === "Enter") send(); });

  // H1：转语音 / 转人工
  root.getElementById("tc-to-voice")?.addEventListener("click", function() {
    if (textWs) { try { textWs.close(); } catch (e) {} textWs = null; }
    renderVoicePanel(storedLocale, storedWidgetId);
  });
  root.getElementById("tc-to-human")?.addEventListener("click", function() {
    var msg = tt("tc.humanRequested");
    chatMessages.push({ role: "agent", text: msg });
    saveMessage("user", "[转人工] 访客请求人工客服");
    saveMessage("agent", msg);
    refreshMessages();
  });
  var qrBtns = root.querySelectorAll(".tc-qr-btn");
  qrBtns.forEach(function(btn) {
    btn.addEventListener("click", function() {
      var reply = (btn as HTMLElement).getAttribute("data-reply") || "";
      if (!reply) return;
      var input = root?.getElementById("tc-input") as HTMLInputElement;
      if (input) { input.value = reply; }
      send();
    });
  });
}

export function closeTextChat(): void { if (textWs) { textWs.close(); textWs = null; } chatMessages = []; removeLoading(); }
